import { useUiStore } from '@/stores';

/**
 * Set the application locale and update all related settings
 * This function handles:
 * 1. Updating the i18n instance locale
 * 2. Storing the locale preference in localStorage
 * 3. Setting HTML attributes for language and direction
 * 4. Adding/removing RTL class for right-to-left languages
 * 
 * @param newLocale - The locale code ('en', 'fr', 'ar')
 * @returns Whether the locale was successfully changed
 */
export function setLocale(newLocale: string): boolean {
  // Validate the locale
  if (!['en', 'fr', 'ar'].includes(newLocale)) {
    return false;
  }
  
  try {
    // Check if we have a valid i18n instance
    if (!window.i18n || !(window.i18n as any).global) {
      return false;
    }
    
    // Set HTML lang attribute for accessibility
    document.documentElement.setAttribute('lang', newLocale);
    
    // Handle RTL for Arabic
    if (newLocale === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
      document.body.classList.add('rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.body.classList.remove('rtl');
    }

    // Keep document metadata synchronized even when the selected locale already
    // matches the active i18n locale (notably during initial application load).
    if ((window.i18n as any).global.locale.value === newLocale) {
      localStorage.setItem('locale', newLocale);
      return true;
    }

    // Set the locale in i18n
    (window.i18n as any).global.locale.value = newLocale;

    // Store in localStorage for persistence
    localStorage.setItem('locale', newLocale);
      // Store messages are handled by Pinia stores now
    // Messages are managed by the UI store
    
    // Force a reactivity refresh in problematic components
    if (typeof Event === 'function') {
      // Create and dispatch a custom event that components can listen for
      window.dispatchEvent(new Event('localeChanged'));
    }
    
    return true;
  } catch (error) {
    return false;
  }
}

export default {};
